import { useState, useCallback, useEffect } from "react";
import {
  Button,
  Typography,
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { createBlobInContainer } from "src/utils/azureBlob";
import { get, post } from "src/utils/networkUtils";
import { handleImageUpload } from "src/utils/handleImageUpload";
import HeadToHeadModeCard from "./HeadToHeadModeCard";
import DailySettings from "./DailySettings";
import BoostSettingsGroup from "./BoostSetting ";
import CardSettings from "./CardSettings";

function a11yProps(index) {
  return {
    id: `settings-tab-${index}`,
    "aria-controls": `settings-tabpanel-${index}`,
  };
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 1, paddingTop: 3 }}>{children}</Box>}
    </div>
  );
}

export default function VersusSettingsDialog({ open, onClose }) {
  const [dialogValue, setDialogValue] = useState(0);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [versusSettings, setVersusSettings] = useState(null);
  const [versusPackType, setVersusPackType] = useState("0");
  const [loading, setLoading] = useState(false);

  const fetchVersusSettings = useCallback(() => {
    setSettingsLoading(true);
    get("/admin/versus/settings")
      .then(function (response) {
        if (response.success && response.settings) {
          setVersusSettings(response.settings);
        } else {
          console.error("Failed to fetch versus settings:", response);
        }
      })
      .catch(function (error) {
        console.error("Error fetching versus settings:", error);
      })
      .finally(() => {
        setSettingsLoading(false);
      });
  }, []);

  const handleChangeDialogTabs = (event, newValue) => {
    setDialogValue(newValue);
  };

  const handleCloseSettings = () => {
    onClose();
  };

  const handleSaveSettings = () => {
    if (!versusSettings) return;

    setSettingsLoading(true);
    post("/admin/versus/settings", { settings: versusSettings })
      .then(function (response) {
        if (response.success) {
          console.log("Versus settings updated successfully");
        } else {
          console.error("Failed to update versus settings:", response);
        }
      })
      .catch(function (error) {
        console.error("Error updating versus settings:", error);
      })
      .finally(() => {
        setSettingsLoading(false);
        onClose();
      });
  };
  const handleAddCard = () => {
    if (!versusSettings || !versusSettings.packSettings) return;

    const newSettings = { ...versusSettings };
    const packSettingIndex = newSettings.packSettings.findIndex(
      (p) => +p.packType === +versusPackType
    );

    if (packSettingIndex !== -1) {
      const newCard = {
        Base: 0,
        Gold: 0,
        Purple: 0,
      };
      newSettings.packSettings[packSettingIndex].probability.push(newCard);
      setVersusSettings(newSettings);
    }
  };

  const handleDeleteCard = (cardIndex) => {
    if (!versusSettings || !versusSettings.packSettings) return;

    const newSettings = { ...versusSettings };
    const packSettingIndex = newSettings.packSettings.findIndex(
      (p) => +p.packType === +versusPackType
    );

    if (
      packSettingIndex !== -1 &&
      newSettings.packSettings[packSettingIndex].probability.length > 1
    ) {
      newSettings.packSettings[packSettingIndex].probability.splice(
        cardIndex,
        1
      );
      setVersusSettings(newSettings);
    }
  };

  useEffect(() => {
    if (open) {
      fetchVersusSettings();
    }
  }, [open, fetchVersusSettings]);

  return (
    <Dialog
      open={open}
      onClose={handleCloseSettings}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="alert-dialog-title">Versus Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={dialogValue}
            onChange={handleChangeDialogTabs}
            aria-label="basic tabs example"
          >
            <Tab label="Head 2 Head" {...a11yProps(0)} />
            <Tab label="Daily" {...a11yProps(1)} />
            <Tab label="Season" {...a11yProps(2)} />
            <Tab label="Pack Settings" {...a11yProps(3)} />
            <Tab label="Boost" {...a11yProps(4)} />
          </Tabs>
        </Box>
        {settingsLoading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography>Loading settings...</Typography>
          </Box>
        ) : (
          <>
            <TabPanel value={dialogValue} index={0}>
              <Grid container spacing={2}>
                {versusSettings &&
                  versusSettings.gameModes &&
                  versusSettings.gameModes
                    .filter((mode) => mode.isHeadToHead)
                    .map((mode, index) => (
                      <HeadToHeadModeCard
                        key={index}
                        mode={mode}
                        index={index}
                        versusSettings={versusSettings}
                        setVersusSettings={setVersusSettings}
                        loading={loading}
                        handleImageUpload={handleImageUpload}
                        createBlobInContainer={createBlobInContainer}
                        setLoading={setLoading}
                      />
                    ))}
              </Grid>
              <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  onClick={handleAddCard}
                  disabled={!versusSettings || !versusSettings.packSettings}
                >
                  Add Card
                </Button>
              </Box>
            </TabPanel>
            <TabPanel value={dialogValue} index={1}>
              {versusSettings &&
                versusSettings.gameModes &&
                versusSettings.gameModes
                  .filter((mode) => mode.gameMode === 5)
                  .map((mode, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      {mode.daily && (
                        <Box sx={{ mt: 3 }}>
                          {mode.daily.map((day, dayIndex) => (
                            <DailySettings
                              key={dayIndex}
                              day={day}
                              dayIndex={dayIndex}
                              versusSettings={versusSettings}
                              setVersusSettings={setVersusSettings}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
            </TabPanel>
            <TabPanel value={dialogValue} index={2}>
              {versusSettings &&
                versusSettings.gameModes &&
                versusSettings.gameModes
                  .filter((mode) => mode.gameMode === 4)
                  .map((mode, index) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 3,
                        p: 2,
                        border: "1px solid #eee",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="subtitle1">{mode.title}</Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          gap: 2,
                          mt: 2,
                        }}
                      >
                        <TextField
                          label="Above FPTS"
                          type="number"
                          value={mode.above}
                          onChange={(e) => {
                            const modeIndex =
                              versusSettings.gameModes.findIndex(
                                (m) => m.gameMode === 4
                              );
                            const newSettings = { ...versusSettings };
                            newSettings.gameModes[modeIndex].above = Number(
                              e.target.value
                            );
                            setVersusSettings(newSettings);
                          }}
                          fullWidth
                          variant="outlined"
                          size="small"
                        />
                        <TextField
                          label="Below FPTS"
                          type="number"
                          value={mode.below}
                          onChange={(e) => {
                            const modeIndex =
                              versusSettings.gameModes.findIndex(
                                (m) => m.gameMode === 4
                              );
                            const newSettings = { ...versusSettings };
                            newSettings.gameModes[modeIndex].below = Number(
                              e.target.value
                            );
                            setVersusSettings(newSettings);
                          }}
                          fullWidth
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </Box>
                  ))}
            </TabPanel>
            <TabPanel value={dialogValue} index={3}>
              <FormControl fullWidth variant="standard">
                <InputLabel id="store-select-label">Pack Type</InputLabel>
                <Select
                  labelId="store-select-label"
                  id="store-select"
                  value={versusPackType}
                  label="Store"
                  onChange={(e) => setVersusPackType(e.target.value)}
                >
                  <MenuItem value="0">Daily Challange Pack</MenuItem>
                  <MenuItem value="1">5 Contracts Pack</MenuItem>
                  <MenuItem value="2">12 Contracts pack</MenuItem>
                </Select>
              </FormControl>
              <Grid container spacing={2} sx={{ mt: 3 }}>
                {versusSettings &&
                  versusSettings.packSettings &&
                  versusSettings.packSettings
                    .find((packType) => +packType.packType === +versusPackType)
                    ?.probability.map((mode, index) => (
                      <Grid
                        item
                        xs={4}
                        sm={4}
                        md={4}
                        key={index}
                        sx={{ mt: 2 }}
                      >
                        <CardSettings
                          key={index}
                          index={index}
                          mode={mode}
                          versusSettings={versusSettings}
                          versusPackType={versusPackType}
                          handleDeleteCard={handleDeleteCard}
                          setVersusSettings={setVersusSettings}
                        />
                      </Grid>
                    ))}
              </Grid>
              <Button
                onClick={handleAddCard}
                variant="contained"
                color="primary"
                size="small"
                sx={{ mt: 2 }}
              >
                Add Card
              </Button>
            </TabPanel>
            <TabPanel value={dialogValue} index={4}>
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <BoostSettingsGroup
                      title="Boost 0-5"
                      startIndex={0}
                      count={6}
                      color="primary.main"
                      versusSettings={versusSettings}
                      setVersusSettings={setVersusSettings}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <BoostSettingsGroup
                      title="Boost 6-10"
                      startIndex={6}
                      count={5}
                      color="secondary.main"
                      versusSettings={versusSettings}
                      setVersusSettings={setVersusSettings}
                    />
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseSettings} disabled={settingsLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveSettings}
          autoFocus
          disabled={settingsLoading}
          variant="contained"
          color="primary"
        >
          {settingsLoading ? "Saving..." : "Save Settings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
